import {
  Box,
  Button,
  Typography,
  TextField,
  Select,
  MenuItem,
  Checkbox,
  ListItemText,
  useMediaQuery,
  CircularProgress,
} from "@mui/material";
import { useState, useRef, useEffect } from "react";
import { Trash2, ArrowLeft, Check } from "lucide-react";
import Footer from "../components/Footer";
import Navbar from "../components/Navbar";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useTranslation } from 'react-i18next';
import { compressImage } from "../utils/imageCompression";
import { uploadHotelDocument, deleteHotelDocument } from "../services/api";
import { useRoom } from "../context/RoomContext";
import { useHotel } from "../context/HotelContext";
import LoadingGif from "../components/LoadingGif";
import CheckList from "../components/CheckList";

const STEPS = [
  { key: "hotelInfo", labelKey: "StepHotelInfo" },
  { key: "licenseVerification", labelKey: "StepLicenseVerification" },
  { key: "roomInfo", labelKey: "StepRoomInfo" },
  { key: "additionalInfo", labelKey: "StepAdditionalInfo" },
  { key: "paymentMethod", labelKey: "StepPaymentMethod" },
];

const CURRENT_STEP_INDEX = 2;
const MAX_FILE_SIZE_BYTES = 10 * 1024 * 1024;

const PET_TYPE_OPTIONS = [
  { key: "dog", labelKey: "AnimalDog" },
  { key: "cat", labelKey: "AnimalCat" },
  { key: "rabbit", labelKey: "AnimalRabbit" },
  { key: "reptile", labelKey: "PetTypeReptile" },
  { key: "smallAnimal", labelKey: "PetTypeSmallAnimal" },
];

const FREE_CANCEL_OPTIONS = [
  { value: "14days", labelKey: "CancelDeadline14" },
  { value: "7days", labelKey: "CancelDeadline7" },
  { value: "3days", labelKey: "CancelDeadline3" },
  { value: "24hours", labelKey: "CancelDeadline24h" },
  { value: "none", labelKey: "CancelDeadlineNone" },
];

const LATE_FEE_OPTIONS = [
  { value: "firstNight", labelKey: "LateFeeFirstNight" },
  { value: "fullAmount", labelKey: "LateFeeFullAmount" },
  { value: "fixed", labelKey: "LateFeeFixed" },
  { value: "nonRefundable", labelKey: "LateFeeNonRefundable" },
];

const HotelAdd_RoomInfo_1 = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { hotelId, roomId } = useParams();
  const isMobile = useMediaQuery("(max-width:950px)");
  const { fetchRoom, updateRoomStep, roomData, loading } = useRoom();
  const { fetchDocuments, fetchDraft } = useHotel();
  const photoInputRef = useRef(null);
  const [FileTooLargeError, setFileTooLargeError] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [searchParams] = useSearchParams();
  const manage = searchParams.get('manage') || '';

  const [formData, setFormData] = useState({
    roomName: "",
    roomQuantity: "",
    dimLength: "",
    dimWidth: "",
    dimHeight: "",
    petTypes: [],
    petQuantity: "",
    description: "",
    weekdayPrice: "",
    weekendPrice: "",
    freeCancelDeadline: "",
    lateCancelFee: "",
    fixedFeePercentage: "",
  });
  const [roomPhotos, setRoomPhotos] = useState([]);
  const [photoUrls, setPhotoUrls] = useState([]);
  const [deletingPhotoIndex, setDeletingPhotoIndex] = useState(null);

  useEffect(() => {
    if (roomId) {
      fetchRoom(roomId);
    }
  }, [roomId, fetchRoom]);

  useEffect(() => {
    if (hotelId) {
      fetchDraft(hotelId);
    }
  }, [hotelId, fetchDraft]);

  useEffect(() => {
    if (roomData?.information1) {
      const info1 = roomData.information1;
      setFormData({
        roomName: info1.roomName || "",
        roomQuantity: info1.roomQuantity?.toString() || "",
        dimLength: info1.dimensions?.length?.toString() || "",
        dimWidth: info1.dimensions?.width?.toString() || "",
        dimHeight: info1.dimensions?.height?.toString() || "",
        petTypes: info1.petTypes || [],
        petQuantity: info1.petQuantity?.toString() || "",
        description: info1.description || "",
        weekdayPrice: info1.weekdayPrice?.toString() || "",
        weekendPrice: info1.weekendPrice?.toString() || "",
        freeCancelDeadline: info1.freeCancelDeadline || "",
        lateCancelFee: info1.lateCancelFee || "",
        fixedFeePercentage: info1.lateCancelFee === "fixed" ? info1.lateCancelFeeValue?.toString() || "" : "",
      });
    }
  }, [roomData?.information1]);

  useEffect(() => {
    if (hotelId && roomId) {
      fetchDocuments().then((response) => {
        const documents = response.data?.data || [];
        const roomPhotos = documents.filter((doc) => doc.type === "roomPhoto" && doc.roomId === roomId);

        if (roomPhotos.length > 0) {
          const photoUrls = roomPhotos.map((doc) => doc.url);
          setPhotoUrls(photoUrls);

          // Create file-like objects for display
          const fileObjs = roomPhotos.map((doc) => ({
            name: doc.fileName || `photo-${doc.documentId}`,
            url: doc.url,
            documentId: doc.documentId
          }));
          setRoomPhotos(fileObjs);
        }
      }).catch((err) => console.error("Failed to fetch room photos:", err));
    }
  }, [hotelId, roomId, fetchDocuments]);

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handlePhotoSelect = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = "";
    if (files.length === 0) return;

    const processed = [];
    for (const file of files) {
      if (file.size > MAX_FILE_SIZE_BYTES) {
        setFileTooLargeError(true);
      }
      else {
        setFileTooLargeError(false);
        const compressed = file.type.startsWith("image/") ? await compressImage(file) : file;
        if (compressed.size <= MAX_FILE_SIZE_BYTES) {
          processed.push(compressed);
        }
      }
    }
    setRoomPhotos((prev) => [...prev, ...processed]);
  };

  const removePhoto = (index) => {
    setRoomPhotos((prev) => prev.filter((_, i) => i !== index));
    setPhotoUrls((prev) => prev.filter((_, i) => i !== index));
  };

  const handleDeleteRoomPhoto = async (index, documentId) => {
    setDeletingPhotoIndex(index);

    try {
      // If documentId exists, delete from server
      if (documentId && hotelId) {
        await deleteHotelDocument(hotelId, documentId);
      }
      // Remove from local state regardless
      removePhoto(index);
    } catch (err) {
      console.error("Failed to delete document:", err);
    } finally {
      setDeletingPhotoIndex(null);
    }
  };

  const handleNext = async () => {
    try {
      setIsUploading(true);

      const information1 = {
        roomName: formData.roomName,
        roomQuantity: parseInt(formData.roomQuantity),
        petTypes: formData.petTypes,
        petQuantity: parseInt(formData.petQuantity),
        weekdayPrice: parseFloat(formData.weekdayPrice),
        weekendPrice: parseFloat(formData.weekendPrice),
        freeCancelDeadline: formData.freeCancelDeadline,
        lateCancelFee: formData.lateCancelFee,
      };

      if (formData.dimLength || formData.dimWidth || formData.dimHeight) {
        information1.dimensions = {
          length: formData.dimLength,
          width: formData.dimWidth,
          height: formData.dimHeight,
        };
      }

      if (formData.description) {
        information1.description = formData.description;
      }

      if (formData.lateCancelFee === "fixed" && formData.fixedFeePercentage) {
        information1.lateCancelFeeValue = parseFloat(formData.fixedFeePercentage);
      }

      await updateRoomStep(roomId, "information1", { information1 });

      // Upload room photos
      for (const photo of roomPhotos) {
        if (photo instanceof File) {
          await uploadHotelDocument(hotelId, photo, "roomPhoto", roomId);
        }
      }

      // Navigate to next page
      if (manage) {
        navigate(`/hotel/${hotelId}/room-info-2/${roomId}?manage=true`);
      } else {
        navigate(`/hotel/${hotelId}/room-info-2/${roomId}`);
      }
    } catch (err) {
      console.error("Error saving room information:", err);
    } finally {
      setIsUploading(false);
    }
  };

  const handleSaveAndLeave = async () => {
    try {
      setIsUploading(true);

      const information1 = {
        roomName: formData.roomName,
        roomQuantity: parseInt(formData.roomQuantity),
        petTypes: formData.petTypes,
        petQuantity: parseInt(formData.petQuantity),
        weekdayPrice: parseFloat(formData.weekdayPrice),
        weekendPrice: parseFloat(formData.weekendPrice),
        freeCancelDeadline: formData.freeCancelDeadline,
        lateCancelFee: formData.lateCancelFee,
      };

      if (formData.dimLength && formData.dimWidth && formData.dimHeight) {
        information1.dimensions = {
          length: parseFloat(formData.dimLength),
          width: parseFloat(formData.dimWidth),
          height: parseFloat(formData.dimHeight),
        };
      }

      if (formData.description) {
        information1.description = formData.description;
      }

      if (formData.lateCancelFee === "fixed" && formData.fixedFeePercentage) {
        information1.lateCancelFeeValue = parseFloat(formData.fixedFeePercentage);
      }

      await updateRoomStep(roomId, "information1", { information1 });

      // Upload room photos
      for (const photo of roomPhotos) {
        if (photo instanceof File) {
          await uploadHotelDocument(hotelId, photo, "roomPhoto", roomId);
        }
      }
      if (manage) {
        navigate(`/hotel/room-management/${hotelId}`);
      } else {
        navigate(`/hotel/onboarding`);
      }
    } catch (err) {
      console.error("Error saving room information:", err);
    } finally {
      setIsUploading(false);
    }
  };

  useEffect(() => {
    const urls = roomPhotos.map((file) => {
      if (file instanceof File) {
        return URL.createObjectURL(file);
      }
      return file.url || null;
    });
    setPhotoUrls(urls);
    return () => {
      urls.forEach((url) => {
        if (url && typeof url === "string" && url.startsWith("blob:")) {
          URL.revokeObjectURL(url);
        }
      });
    };
  }, [roomPhotos]);

  const textFieldSx = {
    "& .MuiOutlinedInput-root": {
      backgroundColor: "#FFFFFF",
      borderRadius: "10px",
    },
  };

  const selectSx = {
    bgcolor: "white",
    borderRadius: "10px",
    height: isMobile ? "45px" : "56px",
    fontSize: isMobile ? "14px" : "16px",
  };

  const labelSx = { fontWeight: "600", fontSize: isMobile ? "14px" : "16px", color: "#000", mb: 1 };

  const isNextDisabled =
    roomPhotos.length < 2 ||
    !formData.roomQuantity ||
    formData.petTypes.length === 0 ||
    !formData.petQuantity ||
    !formData.weekdayPrice ||
    !formData.weekendPrice ||
    !formData.freeCancelDeadline ||
    !formData.lateCancelFee ||
    !formData.roomName ||
    !formData.description ||
    (formData.lateCancelFee === "fixed" && !formData.fixedFeePercentage);

  const pageStyle = {
    display: "flex",
    flexDirection: "column",
    minHeight: "100vh",
    backgroundColor: "#F5F5F5",
  };

  const contentStyle = {
    flex: "1 0 auto",
    margin: "0 auto",
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-start",
    justifyContent: "flex-start",
    padding: isMobile ? "100px 20px 60px" : "120px 80px 80px",
    maxWidth: "1600px",
    width: "100%",
  };

  return (
    <div style={pageStyle}>
      <Navbar />
      <div style={contentStyle}>
        {/* Stepper */}
        <Box sx={{ display: "flex", alignItems: "center", mb: 6, mx: "auto", gap: manage && 1.5, width: manage && isMobile ? "100%" : manage ? "60%" : "auto", }}>
          {manage ? (
            <>
              <Button
                onClick={() => navigate("/hotel/room-management/" + hotelId)}
                sx={{
                  minWidth: "auto",
                  width: isMobile ? "36px" : "44px",
                  height: isMobile ? "36px" : "44px",
                  borderRadius: "50%",
                  backgroundColor: "#31BACD",
                  color: "#fff",
                  padding: 0,
                  "&:hover": { opacity: 0.85, backgroundColor: "#31BACD" },
                }}
              >
                <ArrowLeft size={isMobile ? 18 : 22} />
              </Button>
              <Typography sx={{ fontWeight: "bold", fontSize: isMobile ? "20px" : "24px", color: "#000" }}>
                {t('RoomManagementTitle')} {t('AddaNewRoomTitle')}
              </Typography>
            </>
          ) : (
            STEPS.map((step, index) => (
              <Box key={step.key} sx={{ display: "flex", alignItems: "center", flex: 1 }}>
                <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 1 }}>
                  <Box
                    sx={{
                      width: isMobile ? "20px" : "30px",
                      height: isMobile ? "20px" : "30px",
                      borderRadius: "50%",
                      backgroundColor: index <= CURRENT_STEP_INDEX ? "#31BACD" : "transparent",
                      border: index <= CURRENT_STEP_INDEX ? "3px solid #fff" : "2px solid #D1D1D1",
                      boxShadow: index <= CURRENT_STEP_INDEX ? "0 0 0 2px #31BACD" : "none",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  />
                  <Typography
                    sx={{
                      fontSize: isMobile ? "10px" : "13px",
                      fontWeight: index === CURRENT_STEP_INDEX ? "600" : "400",
                      color: index === CURRENT_STEP_INDEX ? "#000" : "#666",
                      textAlign: "center",
                      whiteSpace: "wrap",
                      width: isMobile ? "50px" : "100px",
                    }}
                  >
                    {t(step.labelKey)}
                  </Typography>
                </Box>
                {index < STEPS.length - 1 && (
                  <Box
                    sx={{
                      height: "1px",
                      width: isMobile ? "30px" : "200px",
                      backgroundColor: "#D1D1D1",
                      mx: "auto",
                      mb: isMobile ? "18px" : "24px",
                    }}
                  />
                )}
              </Box>
            ))
          )}
        </Box>

        <Box sx={{ width: isMobile ? "100%" : "60%", mx: "auto", display: "flex", flexDirection: "column",}}>
          {/* Heading */}
          <Typography sx={{ fontWeight: "bold", fontSize: isMobile ? "20px" : "24px", color: "#000", mb: 4, display: "flex", alignItems: "center", gap: 1 }}>
            {t('RoomInfoStep1Title')}
            <Box component="span" sx={{ fontWeight: "400", fontSize: isMobile ? "12px" : "14px", color: "#666" }}>
              {t('MustFill')} <span style={{ color: "red" }}>*</span><span> )</span>
            </Box>
          </Typography>

          {loading ? (
            <LoadingGif />
          ) : (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 4, width: "100%", }}>
              {/* Room name / quantity */}
              <Box sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 3 }}>
                <Box sx={{ width: "48%" }}>
                  <Typography sx={labelSx}>{t('RoomNameLabel')} <span style={{ color: "red" }}> *</span></Typography>
                  <TextField
                    fullWidth
                    value={formData.roomName}
                    onChange={(e) => handleInputChange("roomName", e.target.value)}
                    sx={textFieldSx}
                  />
                </Box>
                <Box sx={{ width: "48%" }}>
                  <Typography sx={labelSx}>{t('RoomQuantityLabel')} <span style={{ color: "red" }}> *</span></Typography>
                  <TextField
                    fullWidth
                    type="number"
                    value={formData.roomQuantity}
                    onChange={(e) => handleInputChange("roomQuantity", e.target.value)}
                    sx={textFieldSx}
                  />
                </Box>
              </Box>

              {/* Room dimensions */}
              <Box>
                <Typography sx={labelSx}>
                  {t('RoomDimensionsLabel')}{" "}
                  <Box component="span" sx={{ fontWeight: "400", fontSize: isMobile ? "12px" : "13px", color: "#888" }}>
                    {t('RoomDimensionsDesc')}
                  </Box>
                </Typography>
                <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 2 }}>
                  <TextField
                    fullWidth
                    placeholder={t('UnitLength')}
                    value={formData.dimLength}
                    onChange={(e) => handleInputChange("dimLength", e.target.value)}
                    sx={textFieldSx}
                  />
                  <TextField
                    fullWidth
                    placeholder={t('UnitWidth')}
                    value={formData.dimWidth}
                    onChange={(e) => handleInputChange("dimWidth", e.target.value)}
                    sx={textFieldSx}
                  />
                  <TextField
                    fullWidth
                    placeholder={t('UnitHeight')}
                    value={formData.dimHeight}
                    onChange={(e) => handleInputChange("dimHeight", e.target.value)}
                    sx={textFieldSx}
                  />
                </Box>
              </Box>

              {/* Room photos */}
              <Box>
                <Typography sx={labelSx}>{t('RoomPhotosLabel')} <span style={{ color: "red" }}>(2)*</span></Typography>
                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2 }}>
                  {roomPhotos.length === 0 ? (
                    <Box
                      component="label"
                      sx={{
                        width: "100%",
                        borderRadius: "10px",
                        border: "1.5px dashed #D1D1D1",
                        backgroundColor: "#FAFAFA",
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        justifyContent: "center",
                        gap: 1,
                        padding: isMobile ? "24px 16px" : "32px 24px",
                        cursor: "pointer",
                        textAlign: "center",
                      }}
                    >
                      <img
                        src="/icons/upload.svg"
                        alt="Upload"
                        style={{ width: "22px", height: "22px" }}
                      />
                      <Typography sx={{ fontSize: isMobile ? "12px" : "13px", color: "#666", }}>
                        {t('RoomPhotosEmptyDesc')}
                      </Typography>
                      <Typography sx={{ fontSize: isMobile ? "10px" : "11px", color: "#B0B0B0" }}>
                        {t('RoomPhotosFormats')}
                      </Typography>
                      <input
                        ref={photoInputRef}
                        type="file"
                        accept="image/png, image/jpeg, image/webp"
                        multiple
                        hidden
                        onChange={handlePhotoSelect}
                      />
                    </Box>
                  ) : (
                    <Box
                      component="label"
                      sx={{
                        width: isMobile ? "80px" : "100px",
                        height: isMobile ? "80px" : "100px",
                        borderRadius: "10px",
                        border: "1.5px dashed #D1D1D1",
                        backgroundColor: "#FFFFFF",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        cursor: "pointer",
                        flexShrink: 0,
                      }}
                    >
                      <img
                        src="/icons/upload.svg"
                        alt="Upload"
                        style={{ width: "22px", height: "22px" }}
                      />
                      <input
                        ref={photoInputRef}
                        type="file"
                        accept="image/png, image/jpeg, image/webp"
                        multiple
                        hidden
                        onChange={handlePhotoSelect}
                      />
                    </Box>
                  )}
                  {roomPhotos.map((file, index) => (
                    <Box
                      key={`${file.name}-${index}`}
                      sx={{
                        position: "relative",
                        width: isMobile ? "80px" : "100px",
                        height: isMobile ? "80px" : "100px",
                        borderRadius: "10px",
                        overflow: "hidden",
                        flexShrink: 0,
                      }}
                    >
                      <Box
                        component="img"
                        src={photoUrls[index]}
                        alt={file.name}
                        sx={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }}
                      />
                      <Button
                        onClick={() => handleDeleteRoomPhoto(index, file.documentId)}
                        disabled={deletingPhotoIndex === index}
                        sx={{
                          position: "absolute",
                          top: 2,
                          right: 2,
                          minWidth: "auto",
                          width: "22px",
                          height: "22px",
                          padding: 0,
                          borderRadius: "50%",
                          backgroundColor: "rgba(0,0,0,0.55)",
                          color: "#fff",
                          "&:hover": { backgroundColor: "rgba(0,0,0,0.75)" },
                          "&:disabled": { backgroundColor: "rgba(0,0,0,0.55)" },
                        }}
                      >
                        {deletingPhotoIndex === index ? (
                          <CircularProgress size={14} sx={{ color: "#fff" }} />
                        ) : (
                          <Trash2 size={14} />
                        )}
                      </Button>
                    </Box>
                  ))}
                </Box>
                {FileTooLargeError && (
                  <Typography sx={{ fontSize: isMobile ? "12px" : "13px", color: "#D32F2F", mt: 1 }}>
                    {t('FileTooLargeError')}
                  </Typography>
                )}
              </Box>

              {/* Pet type / quantity */}
              <Box sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 3 }}>
                <Box sx={{ width: "48%" }}>
                  <Typography sx={labelSx}>{t('AcceptedPetTypeLabel')} <span style={{ color: "red" }}> * </span>
                    <Box component="span" sx={{ fontWeight: "400", fontSize: isMobile ? "12px" : "13px", color: "#888" }}>
                      {t('AccpetedPetTypes')}
                    </Box>
                  </Typography>
                  <Select
                    multiple
                    displayEmpty
                    fullWidth
                    value={formData.petTypes}
                    onChange={(e) => handleInputChange("petTypes", e.target.value)}
                    renderValue={(selected) =>
                      selected.length ? (
                        selected.map((key) => t(PET_TYPE_OPTIONS.find((o) => o.key === key)?.labelKey)).join(", ")
                      ) : (
                        <Box component="span" sx={{ color: "#9e9e9e" }}>{t('PleaseSelect')}</Box>
                      )
                    }
                    sx={selectSx}
                  >
                    {PET_TYPE_OPTIONS.map((option) => (
                      <MenuItem key={option.key} value={option.key}>
                        <Checkbox checked={formData.petTypes.includes(option.key)} />
                        <ListItemText primary={t(option.labelKey)} />
                      </MenuItem>
                    ))}
                  </Select>
                </Box>
                <Box sx={{ width: "48%" }}>
                  <Typography sx={labelSx}>
                    {t('AcceptedPetQuantityLabel')} <span style={{ color: "red" }}> * </span>
                    <Box component="span" sx={{ fontWeight: "400", fontSize: isMobile ? "12px" : "13px", color: "#888" }}>
                      {t('AcceptedPetQuantityDesc')}
                    </Box>
                  </Typography>
                  <TextField
                    fullWidth
                    type="number"
                    value={formData.petQuantity}
                    onChange={(e) => handleInputChange("petQuantity", e.target.value)}
                    sx={textFieldSx}
                  />
                </Box>
              </Box>

              {/* Description */}
              <Box>
                <Typography sx={labelSx}>
                  {t('RoomDescLabel')}<span style={{ color: "red" }}> *</span>
                </Typography>
                <TextField
                  fullWidth
                  multiline
                  rows={3}
                  value={formData.description}
                  onChange={(e) => handleInputChange("description", e.target.value)}
                  sx={textFieldSx}
                />
              </Box>

              {/* Price */}
              <Box>
                <Typography sx={labelSx}>{t('RoomPriceLabel')}</Typography>
                <Box sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 3 }}>
                  <Box sx={{ width: "48%" }}>
                    <Typography sx={{ fontWeight: "400", fontSize: isMobile ? "14px" : "16px", color: "#7d7d7d", mb: 1 }}>
                      {t('WeekdayPriceLabel')} <span style={{ color: "red" }}> *</span>
                    </Typography>
                    <TextField
                      fullWidth
                      type="number"
                      value={formData.weekdayPrice}
                      onChange={(e) => handleInputChange("weekdayPrice", e.target.value)}
                      sx={textFieldSx}
                    />
                  </Box>
                  <Box sx={{ width: "48%" }}>
                    <Typography sx={{ fontWeight: "400", fontSize: isMobile ? "14px" : "16px", color: "#7d7d7d", mb: 1 }}>
                      {t('WeekendPriceLabel')} <span style={{ color: "red" }}> *</span>
                    </Typography>
                    <TextField
                      fullWidth
                      type="number"
                      value={formData.weekendPrice}
                      onChange={(e) => handleInputChange("weekendPrice", e.target.value)}
                      sx={textFieldSx}
                    />
                  </Box>
                </Box>
              </Box>

              {/* Cancellation policy */}
              <Box sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 3, flexWrap: "wrap" }}>
                <Box sx={{ width: "48%" }}>
                  <Typography sx={labelSx}>{t('FreeCancelDeadlineLabel')} <span style={{ color: "red" }}> *</span></Typography>
                  <Select
                    displayEmpty
                    fullWidth
                    value={formData.freeCancelDeadline}
                    onChange={(e) => handleInputChange("freeCancelDeadline", e.target.value)}
                    renderValue={(selected) =>
                      selected ? (
                        t(FREE_CANCEL_OPTIONS.find((o) => o.value === selected)?.labelKey)
                      ) : (
                        <Box component="span" sx={{ color: "#9e9e9e" }}>{t('PleaseSelect')}</Box>
                      )
                    }
                    sx={selectSx}
                  >
                    {FREE_CANCEL_OPTIONS.map((option) => (
                      <MenuItem key={option.value} value={option.value}>{t(option.labelKey)}</MenuItem>
                    ))}
                  </Select>
                </Box>
                <Box sx={{ width: "48%" }}>
                  <Typography sx={labelSx}>{t('LateCancelFeeLabel')} <span style={{ color: "red" }}> *</span></Typography>
                  <Select
                    displayEmpty
                    fullWidth
                    value={formData.lateCancelFee}
                    onChange={(e) => handleInputChange("lateCancelFee", e.target.value)}
                    renderValue={(selected) =>
                      selected ? (
                        t(LATE_FEE_OPTIONS.find((o) => o.value === selected)?.labelKey)
                      ) : (
                        <Box component="span" sx={{ color: "#9e9e9e" }}>{t('PleaseSelect')}</Box>
                      )
                    }
                    sx={selectSx}
                  >
                    {LATE_FEE_OPTIONS.map((option) => (
                      <MenuItem key={option.value} value={option.value}>{t(option.labelKey)}</MenuItem>
                    ))}
                  </Select>
                  {formData.lateCancelFee === "fixed" && (
                    <Box sx={{ mt: 2 }}>
                      <Typography sx={{ fontWeight: "400", fontSize: isMobile ? "14px" : "16px", color: "#7d7d7d", mb: 1 }}>
                        {t('FixedFeePercentageLabel')} <span style={{ color: "red" }}> *</span>
                      </Typography>
                      <TextField
                        fullWidth
                        type="number"
                        value={formData.fixedFeePercentage}
                        onChange={(e) => handleInputChange("fixedFeePercentage", e.target.value)}
                        sx={textFieldSx}
                      />
                    </Box>
                  )}
                </Box>
              </Box>
            </Box>
          )}
          {/* Bottom actions */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              width: "100%",
              mt: 6,
            }}
          >
            <Button
              onClick={handleSaveAndLeave}
              disabled={isNextDisabled || isUploading}
              sx={{
                color: "#666",
                textTransform: "none",
                fontSize: isMobile ? "14px" : "16px",
                textDecoration: "underline",
                "&:disabled": { opacity: 0.5 },
              }}
            >
              {t('SaveAndLeave')}
            </Button>
            <Box sx={{ display: "flex", gap: 2 }}>
              <Button
                variant="outlined"
                onClick={() => navigate(manage ? `/hotel/room-management/${hotelId}` : `/hotel/add/license-verification-submit/${hotelId}`)}
                sx={{
                  color: "#666",
                  borderColor: "#D1D1D1",
                  borderRadius: "10px",
                  textTransform: "none",
                  fontSize: isMobile ? "14px" : "16px",
                  padding: "10px 32px",
                  "&:hover": { borderColor: "#999", backgroundColor: "transparent" },
                }}
              >
                {t('PreviousStep')}
              </Button>
              <Button
                variant="contained"
                disabled={isNextDisabled || isUploading}
                onClick={handleNext}
                sx={{
                  backgroundColor: "#31BACD",
                  color: "#fff",
                  borderRadius: "10px",
                  textTransform: "none",
                  fontSize: isMobile ? "14px" : "16px",
                  padding: "10px 32px",
                  "&:hover": { opacity: 0.85, backgroundColor: "#31BACD" },
                  "&:disabled": { opacity: 0.5, color: "#fff" },
                }}
              >
                {isUploading ? <CircularProgress size={20} color="inherit" /> : t('NextStep')}
              </Button>
            </Box>
          </Box>
        </Box>
      </div>
      <Footer />
    </div>
  );
};

export default HotelAdd_RoomInfo_1;
